import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import moment from 'moment';

import { APIResult, emptyAPIResult, loadingAPIResult, successAPIResult, errorAPIResult } from '../../lib/redux';
import { triplePSDatasource } from '../../datasource';
import { DriversSchedule, ScheduleItem, DriverSafetySummary, FleetSafetySummaryResponse, TeamSafetySummary } from '../../models';
import { logout } from '../Auth';

export interface DriversState {
  getSchedule: APIResult<DriversSchedule>;
  updateScheduleItemOrder: APIResult<ScheduleItem>;
  getTripTicket: APIResult<string[]>;
  getDriverSafetySummary: APIResult<DriverSafetySummary>;
  getFleetSafetySummary: APIResult<FleetSafetySummaryResponse>;
  getTeamSafetySummary: APIResult<TeamSafetySummary>;
  teamSafetyMutation: APIResult<void>;

  scheduleDate: string;
  schedule?: DriversSchedule;
  driverSafetySummary?: DriverSafetySummary;
  fleetSafetySummary?: FleetSafetySummaryResponse;
  teamSafetySummary?: TeamSafetySummary;
}

const initialState: DriversState = {
  getSchedule: emptyAPIResult(),
  updateScheduleItemOrder: emptyAPIResult(),
  getTripTicket: emptyAPIResult(),
  getDriverSafetySummary: emptyAPIResult(),
  getFleetSafetySummary: emptyAPIResult(),
  getTeamSafetySummary: emptyAPIResult(),
  teamSafetyMutation: emptyAPIResult(),

  scheduleDate: moment().format('YYYY-MM-DD')
}

export const getSchedule = createAsyncThunk(
  'drivers/getSchedule',
  async (request: {date: string, driverId: string}) => {
    return triplePSDatasource.getDriversSchedule(request.date, request.driverId);
  }
);

export const updateScheduleItemOrder = createAsyncThunk(
  'drivers/updateScheduleItemOrder',
  async (request: {id: string, newOrder: number}) => {
    return triplePSDatasource.updateOrderScheduleItem(request.id, request.newOrder);
  }
);

export const getTripTicket = createAsyncThunk(
  'drivers/getTripTicket',
  async (params: {id: string, orderIds: string[]}) => {
    return triplePSDatasource.getTripTicket(params.id, params.orderIds);
  }
);

export const fetchDriverSafetySummary = createAsyncThunk(
  'drivers/getDriverSafetySummary',
  async (params: { driverId: string; offset: number }) => {
    return triplePSDatasource.getDriverSafetySummary(params);
  }
);

export const getFleetSafetySummary = createAsyncThunk(
  'drivers/getFleetSafetySummary',
  async (offset: number) => {
    return triplePSDatasource.getFleetSafetySummary(offset);
  }
);

export const getTeamSafetySummary = createAsyncThunk(
  'drivers/getTeamSafetySummary',
  async (offset: number) => {
    return triplePSDatasource.getTeamSafetySummary(offset);
  }
);

export const rescoreSafetyReport = createAsyncThunk(
  'drivers/rescoreSafetyReport',
  async (punchCardId: string) => {
    return triplePSDatasource.rescoreSafetyReport(punchCardId);
  }
);

export const setTeamSafetyExclusion = createAsyncThunk(
  'drivers/setTeamSafetyExclusion',
  async (params: { driverId: string; payPeriodId: string; reason: string }) => {
    return triplePSDatasource.setTeamSafetyExclusion(params);
  }
);

export const clearTeamSafetyExclusion = createAsyncThunk(
  'drivers/clearTeamSafetyExclusion',
  async (params: { driverId: string; payPeriodId: string }) => {
    return triplePSDatasource.clearTeamSafetyExclusion(params);
  }
);

export const lockTeamSafetyPeriod = createAsyncThunk(
  'drivers/lockTeamSafetyPeriod',
  async (payPeriodId: string) => {
    return triplePSDatasource.lockTeamSafetyPeriod(payPeriodId);
  }
);

export const unlockTeamSafetyPeriod = createAsyncThunk(
  'drivers/unlockTeamSafetyPeriod',
  async (payPeriodId: string) => {
    return triplePSDatasource.unlockTeamSafetyPeriod(payPeriodId);
  }
);

export const driversSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    setScheduleDate(state, payload) { state.scheduleDate = payload.payload },
    resetGetTripTicketl(state) { state.getTripTicket = initialState.getTripTicket }
  },
  extraReducers: builder => {
    builder
      .addCase(getSchedule.pending, state => {
        state.getSchedule = loadingAPIResult();
        state.schedule = initialState.schedule;
      })
      .addCase(getSchedule.fulfilled, (state, action) => {
        state.getSchedule = successAPIResult(action.payload);
        state.schedule = action.payload;
        state.schedule?.items.sort((a, b) => parseInt(a.order) - parseInt(b.order));
      })
      .addCase(getSchedule.rejected, (state, action) => {
        state.getSchedule = errorAPIResult(action.error);
      })
      .addCase(updateScheduleItemOrder.pending, state => {
        state.updateScheduleItemOrder = loadingAPIResult();
      })
      .addCase(updateScheduleItemOrder.fulfilled, (state, action) => {
        state.updateScheduleItemOrder = successAPIResult(action.payload);
      })
      .addCase(updateScheduleItemOrder.rejected, (state, action) => {
        state.updateScheduleItemOrder = errorAPIResult(action.error);
      })
      .addCase(getTripTicket.pending, state => {
        state.getTripTicket = loadingAPIResult();
      })
      .addCase(getTripTicket.fulfilled, (state, action) => {
        state.getTripTicket = successAPIResult(action.payload);
      })
      .addCase(getTripTicket.rejected, (state, action) => {
        state.getTripTicket = errorAPIResult(action.error);
      })
      .addCase(fetchDriverSafetySummary.pending, state => {
        state.getDriverSafetySummary = loadingAPIResult();
        state.driverSafetySummary = undefined;
      })
      .addCase(fetchDriverSafetySummary.fulfilled, (state, action) => {
        state.getDriverSafetySummary = successAPIResult(action.payload);
        state.driverSafetySummary = action.payload;
      })
      .addCase(fetchDriverSafetySummary.rejected, (state, action) => {
        state.getDriverSafetySummary = errorAPIResult(action.error);
      })
      .addCase(getFleetSafetySummary.pending, state => {
        state.getFleetSafetySummary = loadingAPIResult();
      })
      .addCase(getFleetSafetySummary.fulfilled, (state, action) => {
        state.getFleetSafetySummary = successAPIResult(action.payload);
        state.fleetSafetySummary = action.payload;
      })
      .addCase(getFleetSafetySummary.rejected, (state, action) => {
        state.getFleetSafetySummary = errorAPIResult(action.error);
      })
      .addCase(getTeamSafetySummary.pending, state => {
        state.getTeamSafetySummary = loadingAPIResult();
      })
      .addCase(getTeamSafetySummary.fulfilled, (state, action) => {
        state.getTeamSafetySummary = successAPIResult(action.payload);
        state.teamSafetySummary = action.payload;
      })
      .addCase(getTeamSafetySummary.rejected, (state, action) => {
        state.getTeamSafetySummary = errorAPIResult(action.error);
      })
      .addMatcher(
        isAnyOf(
          setTeamSafetyExclusion.pending,
          clearTeamSafetyExclusion.pending,
          lockTeamSafetyPeriod.pending,
          unlockTeamSafetyPeriod.pending,
        ),
        state => {
          state.teamSafetyMutation = loadingAPIResult();
        }
      )
      .addMatcher(
        isAnyOf(
          setTeamSafetyExclusion.fulfilled,
          clearTeamSafetyExclusion.fulfilled,
          lockTeamSafetyPeriod.fulfilled,
          unlockTeamSafetyPeriod.fulfilled,
        ),
        state => {
          state.teamSafetyMutation = successAPIResult(undefined);
        }
      )
      .addMatcher(
        isAnyOf(
          setTeamSafetyExclusion.rejected,
          clearTeamSafetyExclusion.rejected,
          lockTeamSafetyPeriod.rejected,
          unlockTeamSafetyPeriod.rejected,
        ),
        (state, action) => {
          state.teamSafetyMutation = errorAPIResult(action.error);
        }
      )
      .addMatcher(
        isAnyOf(
          logout
        ),
        state => {
          state.getSchedule = initialState.getSchedule;
          state.updateScheduleItemOrder = initialState.updateScheduleItemOrder;
          state.getTripTicket = initialState.getTripTicket;
          state.getDriverSafetySummary = initialState.getDriverSafetySummary;
          state.getFleetSafetySummary = initialState.getFleetSafetySummary;
          state.fleetSafetySummary = undefined;
          state.getTeamSafetySummary = initialState.getTeamSafetySummary;
          state.teamSafetyMutation = initialState.teamSafetyMutation;
          state.teamSafetySummary = undefined;

          state.scheduleDate = initialState.scheduleDate;
          state.schedule = undefined;
          state.driverSafetySummary = undefined;
        }
      )
  }
});

export const { setScheduleDate, resetGetTripTicketl } = driversSlice.actions;
export default driversSlice.reducer;
